import { useEffect, useRef } from "react";

/**
 * useVisibilityPoll — run `fetchFn()` on an interval, but only while the
 * browser tab is visible (`document.hidden === false`).
 *
 * Lifecycle:
 *   - Mount: fire fetchFn immediately; arm interval if tab is visible.
 *   - Tab hidden (user switched tabs / minimised / locked screen):
 *     clearInterval — no wasted API calls.
 *   - Tab visible again: fire fetchFn immediately AND re-arm interval.
 *   - Unmount: remove listener + clear active interval.
 *
 * Implementation notes:
 *   - A ref holds the latest `fetchFn` so the closure in setInterval always
 *     calls the CURRENT version — avoids stale-closure bugs across re-renders.
 *   - `start()` clears any previous interval before creating a new one,
 *     so duplicate intervals are structurally impossible.
 *
 * @param {() => void | Promise<void>} fetchFn — the poll body
 * @param {number} intervalMs — cadence in milliseconds
 */
export default function useVisibilityPoll(fetchFn, intervalMs) {
  const fnRef = useRef(fetchFn);
  useEffect(() => {
    fnRef.current = fetchFn;
  });
  useEffect(() => {
    let timerId = null;
    let cancelled = false;

    const stop = () => {
      if (timerId) {
        clearInterval(timerId);
        timerId = null;
      }
    };
    const tick = () => {
      if (!cancelled && !document.hidden) fnRef.current();
    };
    const start = () => {
      stop();
      timerId = setInterval(tick, intervalMs);
    };
    const onVisibility = () => {
      if (cancelled) return;
      if (document.hidden) {
        stop();
      } else {
        fnRef.current();
        start();
      }
    };

    fnRef.current(); // always fetch once on mount
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [intervalMs]);
}
